import { AlertCircle, CheckCheck, Clock, Instagram, Linkedin, Mail, MailOpen, MousePointerClick, RefreshCw, Send } from "lucide-react";

/**
 * Outreach history list rendered under the AI generator on /app/outreach.
 * Pure presentational — parent owns the `sends` array and refresh callback.
 */

const STATUS_STYLE = {
  queued:    "border-line text-ink-muted",
  sent:      "border-volt/60 text-volt",
  delivered: "border-volt/60 text-volt",
  opened:    "border-white/60 text-white",
  clicked:   "border-white/60 text-white",
  bounced:   "border-[#FF3B30]/60 text-[#FF3B30]",
  complained:"border-[#FF3B30]/60 text-[#FF3B30]",
  failed:    "border-[#FF3B30]/60 text-[#FF3B30]",
};
const CHANNEL_ICON = { email: Mail, instagram_dm: Instagram, linkedin: Linkedin };
const EVENT_META = {
  "email.sent":      { label: "Sent",      Icon: Send },
  "email.delivered": { label: "Delivered", Icon: CheckCheck },
  "email.opened":    { label: "Opened",    Icon: MailOpen },
  "email.clicked":   { label: "Clicked",   Icon: MousePointerClick },
  "email.bounced":   { label: "Bounced",   Icon: AlertCircle },
  "email.complained":{ label: "Complained",Icon: AlertCircle },
};

function eventMeta(t) {
  if (EVENT_META[t]) return EVENT_META[t];
  const short = (t || "").split(".").pop();
  return { label: short ? short[0].toUpperCase() + short.slice(1) : "Event", Icon: Clock };
}

// Plain-English hint per error_kind so athletes know if they can retry vs need
// to fix something. Matches backend _classify_resend_error in outreach.py.
const ERROR_KIND_HINTS = {
  auth_error:        " — your Resend API key is missing or invalid. Contact support.",
  rate_limit:        " — Resend rate-limited this send. Try again in a few minutes.",
  invalid_recipient: " — the brand's email address looks malformed. Update it on the brand record.",
  domain_unverified: " — your sending domain isn't verified on Resend yet.",
  transient:         " — temporary Resend hiccup. Click Send again.",
  bad_request:       " — the request was rejected. Check the subject/body length and try again.",
  unknown:           "",
};

function ErrorKindHint({ kind }) {
  const hint = ERROR_KIND_HINTS[kind];
  if (!hint) return null;
  return <span className="text-ink-muted">{hint}</span>;
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function OutreachHistory({ sends, onRefresh }) {
  return (
    <div className="mt-12">
      <div className="flex justify-between items-end mb-5">
        <div>
          <div className="label-eyebrow">Pitch performance</div>
          <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tighter mt-2">Outreach history</h2>
        </div>
        <button onClick={onRefresh} className="btn-ghost-line inline-flex items-center gap-2 text-xs" data-testid="outreach-history-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {sends.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="outreach-history-empty">
          <Clock className="w-7 h-7 text-ink-muted mx-auto mb-3" />
          <div className="font-heading text-lg uppercase">No outreach sent yet</div>
          <p className="text-ink-muted text-sm mt-2">Generate a pitch and hit Send to start tracking delivery, opens and clicks here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="outreach-history-list">
          {sends.map(s => <SendRow key={s.id} send={s} />)}
        </div>
      )}
    </div>
  );
}

function SendRow({ send }) {
  const ChannelIcon = CHANNEL_ICON[send.channel] || Mail;
  const statusCls = STATUS_STYLE[send.status] || STATUS_STYLE.queued;
  // Build a unified timeline: send creation + provider events (deduped, sorted)
  const baseEvent = { type: `${send.channel}.queued`, at: send.created_at };
  const events = [baseEvent, ...(send.events || [])]
    .filter(e => e.at)
    .sort((a, b) => new Date(a.at) - new Date(b.at));
  return (
    <div className="surface p-5" data-testid={`send-${send.id}`}>
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 border border-line flex items-center justify-center flex-shrink-0">
            <ChannelIcon className="w-4 h-4 text-ink-muted" />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-lg uppercase tracking-tight truncate">{send.brand_name || "Brand"}</div>
            <div className="text-xs text-ink-muted mt-0.5 flex flex-wrap gap-x-3">
              <span>{send.channel.replace("_", " ")}</span>
              {send.recipient && <span className="truncate">{send.recipient}</span>}
              <span>{fmtWhen(send.created_at)}</span>
            </div>
          </div>
        </div>
        <span className={`px-2 py-1 text-[10px] uppercase tracking-widest border ${statusCls}`} data-testid={`send-status-${send.id}`}>
          {send.status}{send.provider === "stub" ? " · stub" : ""}
        </span>
      </div>
      {send.subject && <div className="mt-3 text-sm text-white truncate">Subject: <span className="text-ink-muted">{send.subject}</span></div>}
      {send.from_header && (
        <div className="mt-2 text-xs text-ink-muted font-mono break-all" data-testid={`send-from-${send.id}`}>
          From: <span className="text-white">{send.from_header}</span>
          {send.reply_to && <> · Reply-To: <span className="text-white">{send.reply_to}</span></>}
        </div>
      )}
      {send.error && (
        <div className="mt-2 text-xs text-[#FF3B30]" data-testid={`send-error-${send.id}`}>
          {send.error_kind && <span className="font-mono mr-1.5">[{send.error_kind}]</span>}
          {send.error}
          {send.error_kind && <ErrorKindHint kind={send.error_kind} />}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-line">
        <div className="label-eyebrow mb-3">Timeline</div>
        <ol className="space-y-2" data-testid={`send-timeline-${send.id}`}>
          {events.map((ev, i) => {
            const { label, Icon } = eventMeta(ev.type);
            return (
              <li key={i} className="flex items-center gap-3 text-xs">
                <div className="w-7 h-7 border border-line flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-volt" />
                </div>
                <div className="text-white">{label}</div>
                <div className="text-ink-muted font-mono">{fmtWhen(ev.at)}</div>
              </li>
            );
          })}
          {events.length === 1 && (
            <li className="text-xs text-ink-muted pl-10">Awaiting provider events — connect Resend and configure webhooks to track delivery, opens & clicks.</li>
          )}
        </ol>
      </div>
    </div>
  );
}
